/** Custom config-editor widgets for the Ops Console integration editor.
 *
 * The schema-driven config grid renders one row per config key and supports a
 * per-row custom-widget override via `renderValue`. The parent wires
 * `integrationWidget` in: it inspects the row's `x-widget` schema hint and
 * returns the matching widget (or `null`, so the grid falls back to its default
 * cell for unknown/absent widgets).
 *
 * Widget → x-widget mapping:
 *   integration-preset → IntegrationPresetWidget (select + what-it-writes viewer)
 *   import-source     → ImportSourceWidget      (inline single-select)
 *   tag-category-list → TagCategoryListWidget    (inline multi-select)
 *   questions         → QuestionsWidget          (inline key → tag-category rows)
 *   merge-mode        → MergeModeWidget          (inline tag-category → mode rows)
 *   resources         → ResourcesCell            (summary + dialog)
 *   tag-mapping       → TagMappingCell           (summary + dialog)
 *   user-key          → UserKeyCell              (summary + dialog)
 *   json              → JsonEditorCell           (summary + dialog)
 */
import type { ReactNode } from 'react';

import type { ConfigRow } from '@/features/tour-operators/config/schema-grid/types';

import { ImportSourceWidget } from './ImportSourceWidget';
import { IntegrationPresetWidget } from './IntegrationPresetWidget';
import { JsonEditorCell } from './JsonEditorDialog';
import { MergeModeWidget } from './MergeModeWidget';
import { ObjectFormCell } from './ObjectFormDialog';
import { QuestionsWidget } from './QuestionsWidget';
import { ResourcesCell } from './ResourcesEditorDialog';
import { TagCategoryListWidget } from './TagCategoryListWidget';
import { TagMappingCell } from './TagMappingEditorDialog';
import { UserKeyCell } from './UserKeyEditorDialog';

export { ImportSourceWidget } from './ImportSourceWidget';
export { IntegrationPresetWidget } from './IntegrationPresetWidget';
export { JsonEditorCell } from './JsonEditorDialog';
export { MergeModeWidget } from './MergeModeWidget';
export { ObjectFormCell } from './ObjectFormDialog';
export { QuestionsWidget } from './QuestionsWidget';
export { ResourcesCell } from './ResourcesEditorDialog';
export { TagCategoryAutocomplete } from './TagCategoryAutocomplete';
export { TagCategoryListWidget } from './TagCategoryListWidget';
export { TagMappingCell } from './TagMappingEditorDialog';
export { UserKeyCell } from './UserKeyEditorDialog';

interface WidgetArgs {
  row: ConfigRow;
  onChange: (v: unknown) => void;
  readOnly: boolean;
  toId: number;
  /** The integration this config belongs to — the preset widget lists only
   *  that restech's presets, since a preset from another one is ignored by the
   *  backend anyway. */
  restech?: string | null;
}

/** Coerce an arbitrary stored value into a `string | null` code. */
function asCode(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null;
}

/** Coerce an arbitrary stored value into `string[]` of codes. */
function asCodeList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string');
}

/**
 * Dispatch a config row to its custom widget based on `schema['x-widget']`.
 * Returns `null` for unknown/absent widgets so the grid renders its default cell.
 */
export function integrationWidget({
  row,
  onChange,
  readOnly,
  toId,
  restech,
}: WidgetArgs): ReactNode | null {
  const widget = row.schema?.['x-widget'];
  if (!widget) return null;

  switch (widget) {
    case 'integration-preset':
      return (
        <IntegrationPresetWidget
          restech={restech ?? null}
          value={row.value}
          onChange={onChange}
          readOnly={readOnly}
        />
      );

    case 'import-source':
      return (
        <ImportSourceWidget
          toId={toId}
          value={asCode(row.value)}
          onChange={onChange}
          readOnly={readOnly}
        />
      );

    case 'tag-category-list':
      return (
        <TagCategoryListWidget
          toId={toId}
          value={asCodeList(row.value)}
          onChange={onChange}
          readOnly={readOnly}
        />
      );

    case 'questions':
      return (
        <QuestionsWidget toId={toId} value={row.value} onChange={onChange} readOnly={readOnly} />
      );

    case 'resources':
      return (
        <ResourcesCell toId={toId} value={row.value} onChange={onChange} readOnly={readOnly} />
      );

    case 'tag-mapping':
      return (
        <TagMappingCell toId={toId} value={row.value} onChange={onChange} readOnly={readOnly} />
      );

    case 'merge-mode':
      return (
        <MergeModeWidget toId={toId} value={row.value} onChange={onChange} readOnly={readOnly} />
      );

    case 'user-key':
      return <UserKeyCell value={row.value} onChange={onChange} readOnly={readOnly} />;

    case 'json':
      return <JsonEditorCell value={row.value} onChange={onChange} readOnly={readOnly} />;

    case 'object-form':
      return (
        <ObjectFormCell
          schema={row.schema}
          value={row.value}
          onChange={onChange}
          readOnly={readOnly}
          title={row.key}
        />
      );

    default:
      return null;
  }
}
